/**
 * Onboarding step model + local-state persistence (Phase 65 / BLU-594 PR4;
 * BLU-661 client variant).
 *
 * Two flows share one step model:
 *  - STEP_IDS        — the owner/POD "no Shopify → printing correctly" walk.
 *  - CLIENT_STEP_IDS — the 3PL-client variant (org with `ownedLocationCount === 0`):
 *                      connect Shopify → claim a shared location → tag products
 *                      (+ whose garments they print on) → Shopify fields → code
 *                      tables → product sync → blank-garment products → blank
 *                      inventory.
 *                      Colour aliases / RIP routing / test order are dropped (a
 *                      store-only client never runs them), so the gated walk MUST
 *                      run over the ACTIVE set. A strict sequential unlock over the
 *                      full set would read a client's permanently-0-row RIP / alias
 *                      steps as incomplete and lock everything after them — hence
 *                      `computeUnlocked` takes the active step list.
 *
 * BLU-1371 / BLU-1373: the client flow's "Tag products" step (`syncTag`)
 * saves the product sync tag and two answers in one call: what the client
 * sells (`sells`: ready-made designs, custom or both) and whose garments it
 * prints on (`blankSource`: the 3PL's, its own or both). The step is complete
 * once all three are saved.
 *
 * The client flow shows only the steps its answers need. `activeStepIds` is
 * the ONE visible list (wizard nodes, numbers, progress, unlock walk, poll,
 * sidebar badge):
 *  - `typeKinds` (Custom or ready-made, per product type) sits right after
 *    Tag products and is shown ONLY when the client sells both.
 *  - With the 3PL's garments (`supply`), code tables, blank products and blank
 *    inventory are HIDDEN (`computeNotNeeded`), never shown Skipped. A hidden
 *    step is out of the list, so it never locks a later step or counts toward
 *    progress. Own, both or never chose keeps all three.
 *  - `customFiles` ("How do your custom files arrive?", BLU-1399) sits right
 *    after Sync products and is shown ONLY when the client sells custom or
 *    both.
 * Every rule is CLIENT-FLOW ONLY (keyed on the list including `syncTag`), so an
 * owner org whose settings carry stale answers is unaffected. The owner flow
 * ALWAYS shows `customFiles` right after Sync products (CF-5), with a "We
 * don't sell custom products" choice that finishes it.
 *
 * A live client that synced products before the step existed (it synced
 * products or ran a product sync) reads Tag products as complete (Skipped), so
 * nothing regresses to To do or locks.
 *
 * A client that sells custom only writes no print rows (custom products need
 * no designs), so its sync step also completes when its latest job completed
 * with at least one product done (`customSyncJobDone`). `mayHaveSyncedProducts` tells the wizard
 * when a save that changes how products get SKUs must ask first.
 *
 * Most completion signals come from the server (`getOnboardingStatus`); every
 * step also accepts a manual "Mark as done" override (an operator decision the
 * server can't know), and colour aliases can be skipped. Since BLU-1376
 * (Phase 95) those flags live on the server (`stepFlags`, table
 * `org_setup_steps`) and reach these helpers as `StepDecisions` through
 * `decisionsFromFlags`. Nothing here reads browser storage; the old per-browser
 * localStorage flags are ignored and not imported (Q5).
 */
import { SETUP_STEP_IDS, type SetupStepId } from '@threadheads/shared';
import type { RouterOutputs } from '@/lib/trpc';

export type OnboardingStatus = RouterOutputs['productSync']['getOnboardingStatus'];

/** Every wizard step id: the server's vocabulary (`org_setup_steps.step_id`), one list. */
export type StepId = SetupStepId;

/** Owner/POD flow — the original seven-step walk (unchanged order). */
export const STEP_IDS: readonly StepId[] = [
  'shopify',
  'codeTables',
  'metafields',
  'sync',
  'customFiles',
  'colourAliases',
  'ripRouting',
  'testOrder',
] as const;

/**
 * 3PL-client flow (`ownedLocationCount === 0`). Claim slots in right after
 * Shopify — `claimSharedLocation` validates the caller's real Shopify
 * locations, so a store connection must exist first. Code tables stay in the
 * client flow (product sync derives SKUs from colour/size codes — "product-sync
 * with colour codes" in the BLU-657 decided order). Colour aliases, RIP
 * routing and the test order are owner-only and dropped; blank-garment products
 * + blank inventory are appended. Not every id is visible: `activeStepIds`
 * hides `typeKinds` unless the client sells both, `customFiles` unless it
 * sells custom or both, and the three garment steps when it uses the 3PL's
 * garments.
 *
 * `customFiles` (BLU-1399, CC-4) sits right after `sync`, so after the claim
 * (the key is saved per 3PL) and after the products are synced (the key and
 * the colour codes come after them). With the 3PL's garments it is the last
 * visible step.
 */
export const CLIENT_STEP_IDS: readonly StepId[] = [
  'shopify',
  'claim',
  'syncTag',
  'typeKinds',
  'metafields',
  'codeTables',
  'sync',
  'customFiles',
  'blankProducts',
  'blankInventory',
] as const;

/**
 * Optional steps: shown in the walk but never counted toward finishing setup.
 * `inviteTeam` (Invite your team) is appended to either flow by
 * `selectSetupProgress` when the caller can invite (`canInvite`); it is always
 * open, has Mark as done and no Skip, and is left out of `doneCount`, `total`,
 * `allComplete` and `currentStep`, so the band meter, the Back to setup bar,
 * the sidebar meter, the status poll and the finish latch never wait on it.
 * `STEP_IDS` / `CLIENT_STEP_IDS` stay the required walk.
 */
export const OPTIONAL_STEP_IDS: ReadonlySet<StepId> = new Set<StepId>(['inviteTeam']);

/** Union of every step across both flows — used to seed full completion records. */
export const ALL_STEP_IDS: readonly StepId[] = SETUP_STEP_IDS;

/** The tag a client's onboarding offers when the org has none saved yet. */
export const ONBOARDING_SYNC_TAG = 'PrintRun';

/** Client steps that do not apply when the client prints on the 3PL's garments. */
export const NOT_NEEDED_WITH_3PL_GARMENTS: readonly StepId[] = ['codeTables', 'blankProducts', 'blankInventory'] as const;

function allFalse(): Record<StepId, boolean> {
  return Object.fromEntries(ALL_STEP_IDS.map((id) => [id, false])) as Record<StepId, boolean>;
}

/**
 * Steps that do not apply to this org: the three `NOT_NEEDED_WITH_3PL_GARMENTS`
 * ids, ONLY in the client flow (`stepIds` includes `syncTag`) and ONLY when the
 * saved garment choice is `supply`. Optional-chained for deploy skew (a backend
 * without `blankSource` reads as "never chose").
 */
export function computeNotNeeded(
  status: Pick<OnboardingStatus, 'blankSource'>,
  stepIds: readonly StepId[],
): Record<StepId, boolean> {
  const out = allFalse();
  if (!stepIds.includes('syncTag') || (status.blankSource ?? null) !== 'supply') return out;
  for (const id of NOT_NEEDED_WITH_3PL_GARMENTS) out[id] = true;
  return out;
}

/** Tag, what you sell and garment choice all saved (the syncTag step's own signal). */
function syncTagSaved(status: OnboardingStatus): boolean {
  return (
    (status.syncTag ?? '') !== '' && (status.sells ?? null) !== null && (status.blankSource ?? null) !== null
  );
}

/**
 * The org already synced products or ran a product sync (any status), so it
 * onboarded before the tag step existed. Optional-chained for deploy skew.
 */
function hasSyncedBefore(status: OnboardingStatus): boolean {
  return (status.syncedProducts?.lookupRows ?? 0) > 0 || (status.lastSyncJob ?? null) !== null;
}

/**
 * The org may already have synced products, so a save that changes how its
 * products get SKUs must ask first: it synced products or ran a product sync
 * (`hasSyncedBefore`, which also covers a custom-only client whose products
 * wrote no print rows), or the server could not tell (`syncStatusUnavailable`).
 * Optional-chained for deploy skew (a backend without the flag reads as known).
 */
export function mayHaveSyncedProducts(status: OnboardingStatus | undefined): boolean {
  if (!status) return true;
  return hasSyncedBefore(status) || status.syncStatusUnavailable === true;
}

/**
 * The custom-only sync rule, written once: a client that sells custom only
 * writes no print rows (custom products need no designs), so its sync is done
 * when its latest job completed with at least one product done. Takes only
 * the fields it reads, so the wizard status and the sync step props both fit.
 * Optional-chained for deploy skew (a backend without `productsDone` fails
 * closed).
 */
export function customSyncJobDone(status: {
  sells?: OnboardingStatus['sells'] | null;
  lastSyncJob?: { status: string; productsDone?: number } | null;
}): boolean {
  return (
    (status.sells ?? null) === 'custom' &&
    status.lastSyncJob?.status === 'completed' &&
    (status.lastSyncJob?.productsDone ?? 0) > 0
  );
}

/** Owner steps only Threadheads runs (D12, `getOnboardingStatus.ripSteps`). */
export const RIP_STEP_IDS: readonly StepId[] = ['colourAliases', 'ripRouting'] as const;

export interface ActiveStepOptions {
  /** The status's `ripSteps`; anything but `true` drops `RIP_STEP_IDS` from owner lists. */
  ripSteps: boolean;
}

/**
 * The VISIBLE step list for a flow, the one list the wizard, its unlock walk,
 * its poll and the sidebar badge all use.
 *  - Owner lists (no `syncTag`) drop `colourAliases` and `ripRouting` unless
 *    `opts.ripSteps` is true (D12, Threadheads-only); with it they come back
 *    unchanged (same reference).
 *    The owner list never drops `customFiles` (CF-5: a self-printing org
 *    always says how its custom files arrive, or that it sells none).
 *  - Client lists drop `typeKinds` unless the client sells both, drop
 *    `customFiles` unless it sells custom or both, and drop the three garment
 *    steps when it uses the 3PL's garments (`computeNotNeeded`).
 * When nothing is dropped the input list comes back as is; otherwise a new
 * filtered array. Optional-chained for deploy skew (a status
 * without the new fields, or no status yet, hides only `typeKinds`).
 */
export function activeStepIds(
  stepIds: readonly StepId[],
  status: Pick<OnboardingStatus, 'sells' | 'blankSource'> | undefined,
  opts: ActiveStepOptions,
): readonly StepId[] {
  if (!stepIds.includes('syncTag')) {
    // Owner list. The colour-alias and RIP-routing steps are Threadheads-only
    // (D12): dropped unless the status says `ripSteps: true`.
    if (opts.ripSteps === true) return stepIds;
    return stepIds.filter((id) => !RIP_STEP_IDS.includes(id));
  }
  const sells = status?.sells ?? null;
  const supply = status ? computeNotNeeded(status, stepIds).codeTables : false;
  const drop: StepId[] = [];
  if (sells !== 'both') drop.push('typeKinds');
  if (sells !== 'custom' && sells !== 'both') drop.push('customFiles');
  if (supply) drop.push(...NOT_NEEDED_WITH_3PL_GARMENTS);
  return drop.length === 0 ? stepIds : stepIds.filter((id) => !drop.includes(id));
}

/** The operator's Mark as done / Skip decisions, by step (from the server's `stepFlags`). */
export interface StepDecisions {
  /** Steps the operator manually marked done (any step; overrides the server signal). */
  done: Partial<Record<StepId, boolean>>;
  /** Steps the operator explicitly skipped (only `colourAliases` is skippable today). */
  skips: Partial<Record<StepId, boolean>>;
}

/**
 * Per-step completion from the server status + the step decisions (manual done
 * always wins). Computes EVERY step across both flows; the active flow only
 * reads the subset it renders. Server counts are optional-chained / nullish so a
 * backend still serving the pre-BLU-661 shape (deploy skew) reads the new steps
 * as incomplete instead of crashing.
 */
export function computeCompletion(
  status: OnboardingStatus,
  local: StepDecisions,
  stepIds: readonly StepId[],
): Record<StepId, boolean> {
  // A hidden garment step still reads complete for any caller that indexes it.
  const notNeeded = computeNotNeeded(status, stepIds);
  // Custom-only client (client flow only): see `customSyncJobDone`.
  const customJobDone = stepIds.includes('syncTag') && customSyncJobDone(status);
  return {
    shopify: (status.shopify.domainSet && status.shopify.hasCredentials) || !!local.done.shopify,
    claim: (status.activeJunctionCount ?? 0) > 0 || !!local.done.claim,
    // Tag + both answers saved, or (grandfathered) the org already synced
    // products or ran a product sync, or a manual done.
    syncTag: syncTagSaved(status) || hasSyncedBefore(status) || !!local.done.syncTag,
    // A client that sells both has at least one Custom or Mixed type.
    typeKinds: (status.typeKinds?.custom ?? 0) + (status.typeKinds?.mixed ?? 0) > 0 || !!local.done.typeKinds,
    codeTables:
      notNeeded.codeTables ||
      (status.codeTables.productTypes > 0 && status.codeTables.colours > 0 && status.codeTables.sizes > 0) ||
      !!local.done.codeTables,
    // Optional-chained: a backend still serving the pre-metafields shape
    // (deploy skew) must read as incomplete, not crash the wizard.
    metafields: status.metafields?.missingPurposes?.length === 0 || !!local.done.metafields,
    sync: status.syncedProducts.lookupRows > 0 || customJobDone || !!local.done.sync,
    colourAliases: status.colourAliases.rows > 0 || !!local.skips.colourAliases || !!local.done.colourAliases,
    ripRouting: status.ripRouting.productTypes > 0 || !!local.done.ripRouting,
    testOrder: !!local.done.testOrder,
    // Step flag only (a passing Save or "We don't sell custom products" sets it).
    customFiles: !!local.done.customFiles,
    blankProducts: notNeeded.blankProducts || (status.blankProductCount ?? 0) > 0 || !!local.done.blankProducts,
    blankInventory: notNeeded.blankInventory || (status.blankInventoryCount ?? 0) > 0 || !!local.done.blankInventory,
    // Optional (OPTIONAL_STEP_IDS): Mark as done only; a skipped flag never completes it.
    inviteTeam: !!local.done.inviteTeam,
  };
}

/**
 * A step reads as "skipped" (vs genuinely done) when it's complete only via the
 * skip flag — a manual "Mark as done" shows Done, not Skipped. Also Skipped:
 * the tag step when it is complete only because the org already synced
 * products or ran a product sync (a live client from before the step existed).
 * Steps a client does not need are hidden by `activeStepIds`, never Skipped.
 */
export function computeSkipped(status: OnboardingStatus, local: StepDecisions): Record<StepId, boolean> {
  return {
    shopify: false,
    claim: false,
    syncTag: !syncTagSaved(status) && hasSyncedBefore(status) && !local.done.syncTag,
    typeKinds: false,
    codeTables: false,
    metafields: false,
    sync: false,
    colourAliases: !!local.skips.colourAliases && status.colourAliases.rows === 0 && !local.done.colourAliases,
    ripRouting: false,
    testOrder: false,
    customFiles: false,
    blankProducts: false,
    blankInventory: false,
    // No Skip on the optional invite step.
    inviteTeam: false,
  };
}

/**
 * Step N is unlocked once step N-1 is complete (step 1 always unlocked) — walked
 * over the ACTIVE `stepIds`, never the module constant, so a flow that drops
 * middle steps (the client variant) can't be locked out by a dropped step's
 * permanently-zero signal. Steps outside the active set default to locked.
 * An optional step (`OPTIONAL_STEP_IDS`) is always unlocked and never gates
 * the steps after it.
 */
export function computeUnlocked(
  completion: Record<StepId, boolean>,
  stepIds: readonly StepId[],
  opts?: UnlockOptions,
): Record<StepId, boolean> {
  const out = allFalse();
  if (opts?.latched) {
    // D7: once setup is finished every visible step stays open for good.
    for (const id of stepIds) out[id] = true;
    return out;
  }
  let prevComplete = true;
  for (const id of stepIds) {
    if (OPTIONAL_STEP_IDS.has(id)) {
      out[id] = true;
      continue;
    }
    out[id] = prevComplete;
    // A waiting step (handover 4.2) unlocks the next one without counting as done.
    prevComplete = completion[id] || opts?.waiting?.[id] === true;
  }
  return out;
}

export interface UnlockOptions {
  /** Steps submitted and waiting on someone else; they unlock the next step. */
  waiting?: Partial<Record<StepId, boolean>>;
  /** Setup is finished (`setup_completed_at` set): every visible step is unlocked. */
  latched?: boolean;
}

// ---------------------------------------------------------------------------
// Server-backed progress (BLU-1376, Phase 95). Mark as done / Skip come from
// the status's `stepFlags` (D13), never from browser storage (Q5: the old
// per-browser flags are not read and not imported). Setup state
// (`setup_required`, `setup_completed_at`) comes from the org context; the
// status carries `printMode`, `stepFlags`, `ripSteps` and the live signals.
// ---------------------------------------------------------------------------

export type SetupFlow = 'client' | 'owner' | 'question' | 'loading';

export interface SelectFlowInput {
  /** The saved answer to "How do you print?" (from the status). */
  printMode: OnboardingStatus['printMode'] | null | undefined;
  /** `organization.setup_required` from the org context. */
  setupRequired: boolean;
  /** Server-computed pure 3PL client (`useOrgRoleSurfaces`). */
  isPureClient: boolean;
  /** The role surfaces have not answered yet. */
  surfacesLoading: boolean;
}

/**
 * Which flow the wizard runs (handover 4.4, D2). A saved print mode wins; an
 * org still in setup with no answer gets the question alone; a live org keeps
 * today's rule (pure client runs the client flow), waiting for the role
 * surfaces so a client never flashes the owner list.
 */
export function selectFlow({ printMode, setupRequired, isPureClient, surfacesLoading }: SelectFlowInput): SetupFlow {
  if (printMode === 'client') return 'client';
  if (printMode === 'self') return 'owner';
  if (setupRequired) return 'question';
  if (surfacesLoading) return 'loading';
  return isPureClient ? 'client' : 'owner';
}

/**
 * The server's per-step flags in the `{ done, skips }` shape `computeCompletion`
 * and `computeSkipped` take: `'done'` → done, `'skipped'` → skips.
 */
export function decisionsFromFlags(stepFlags: OnboardingStatus['stepFlags'] | undefined | null): StepDecisions {
  const done: Partial<Record<StepId, boolean>> = {};
  const skips: Partial<Record<StepId, boolean>> = {};
  for (const [id, state] of Object.entries(stepFlags ?? {})) {
    if (!(ALL_STEP_IDS as readonly string[]).includes(id)) continue;
    if (state === 'done') done[id as StepId] = true;
    else if (state === 'skipped') skips[id as StepId] = true;
  }
  return { done, skips };
}

/**
 * Steps submitted and waiting on someone else (handover 4.2). Today only the
 * claim: visible, at least one pending request, no active junction yet and
 * not marked done (an operator's Mark as done wins over the amber state).
 * Waiting unlocks the next step but never counts as done.
 */
export function computeWaiting(
  status: Pick<OnboardingStatus, 'pendingJunctionCount' | 'activeJunctionCount'> | undefined,
  stepIds: readonly StepId[],
  decisions?: Pick<StepDecisions, 'done'>,
): Record<StepId, boolean> {
  const out = allFalse();
  if (!status || !stepIds.includes('claim') || decisions?.done.claim) return out;
  out.claim = (status.pendingJunctionCount ?? 0) > 0 && (status.activeJunctionCount ?? 0) === 0;
  return out;
}

export interface SelectSetupProgressInput {
  status: OnboardingStatus | undefined;
  /** `organization.setup_required` from the org context. */
  setupRequired: boolean;
  /** `organization.setup_completed_at` from the org context (or the finish call's return). */
  setupCompletedAt: string | null | undefined;
  isPureClient: boolean;
  surfacesLoading: boolean;
  /**
   * The caller can invite (`can('users.manage')`): the optional `inviteTeam`
   * step is appended to the visible list. Default false. Only the wizard page
   * passes it; it never changes `doneCount`, `total`, `allComplete` or
   * `currentStep`, so callers that omit it (the sidebar meter, the Back to
   * setup bar) show the same numbers.
   */
  canInvite?: boolean;
}

export interface SetupProgress {
  flow: SetupFlow;
  /** The VISIBLE steps, in walk order. Empty while the flow is `question` or `loading`. */
  stepIds: readonly StepId[];
  completion: Record<StepId, boolean>;
  skipped: Record<StepId, boolean>;
  waiting: Record<StepId, boolean>;
  unlocked: Record<StepId, boolean>;
  /** Complete required visible steps (waiting excluded; the optional invite step is left out). */
  doneCount: number;
  /** Required visible steps (the optional invite step is left out). */
  total: number;
  /** A progress meter makes sense (a step flow with at least one step). */
  showMeter: boolean;
  /** Every required visible step complete (a waiting step is not; the optional invite step is left out). */
  allComplete: boolean;
  /**
   * The first required visible step that is neither complete nor waiting; the
   * last required step when none; null with no steps. Never the optional
   * invite step.
   */
  currentStep: StepId | null;
  /** Setup finished (D7). Computed here and nowhere else. */
  latched: boolean;
}

/**
 * The ONE progress derivation, used by the page hook and the sidebar so their
 * flow, list and counts can never drift. Runs `selectFlow` itself.
 */
export function selectSetupProgress({
  status,
  setupRequired,
  setupCompletedAt,
  isPureClient,
  surfacesLoading,
  canInvite = false,
}: SelectSetupProgressInput): SetupProgress {
  const latched = Boolean(setupCompletedAt);
  const flow: SetupFlow = status
    ? selectFlow({ printMode: status.printMode ?? null, setupRequired, isPureClient, surfacesLoading })
    : 'loading';

  if (!status || flow === 'question' || flow === 'loading') {
    const empty = allFalse();
    return {
      flow,
      stepIds: [],
      completion: empty,
      skipped: empty,
      waiting: empty,
      unlocked: empty,
      doneCount: 0,
      total: 0,
      showMeter: false,
      allComplete: false,
      currentStep: null,
      latched,
    };
  }

  const base = flow === 'client' ? CLIENT_STEP_IDS : STEP_IDS;
  const required = activeStepIds(base, status, { ripSteps: status.ripSteps === true });
  const stepIds: readonly StepId[] = canInvite ? [...required, 'inviteTeam'] : required;
  const decisions = decisionsFromFlags(status.stepFlags);
  const completion = computeCompletion(status, decisions, stepIds);
  const skipped = computeSkipped(status, decisions);
  const waiting = computeWaiting(status, stepIds, decisions);
  const unlocked = computeUnlocked(completion, stepIds, { waiting, latched });
  // `required` is the visible list without the optional steps.
  const doneCount = required.filter((id) => completion[id]).length;
  const total = required.length;
  const currentStep =
    required.find((id) => !completion[id] && !waiting[id]) ?? required[required.length - 1] ?? null;

  return {
    flow,
    stepIds,
    completion,
    skipped,
    waiting,
    unlocked,
    doneCount,
    total,
    showMeter: total > 0,
    allComplete: total > 0 && doneCount === total,
    currentStep,
    latched,
  };
}
